import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, signal } from '@angular/core';
import { Location } from '@angular/common';
import { FlowChooserScreening, ScreeningFlow } from './flows/flow-chooser-screening/flow-chooser-screening';
import { AiFirstScreening } from './flows/ai-first-screening/ai-first-screening';
import { WizardFirstScreening } from './flows/wizard-first-screening/wizard-first-screening';
import { CreateSocialScreeningBriefDto, CreateSocialScreeningListDto, ScreeningListCreatorItem, ScreeningRestrictionItem } from '@/app/common/general-types/social-screening.dto';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { FirebaseService } from '@/app/common/firebase/firebase.service';
import { RecruitGroupService } from '@/app/services/recruit-group/recruit-group.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { SocialScreeningService } from '@/app/services/social-screening/social-screening.service';
import { Router } from '@angular/router';
import { AlertService } from '@/app/atoms/alert/alert.service';
import { ScreeningOverlay } from './overlays/screening-overlay/screening-overlay';
import { SUBMIT_STAGES } from './screening-brief.constants';
import { Observable, of, Subscription, switchMap, map, throwError } from 'rxjs';
import { BriefService } from '@/app/services/brief/brief.service';
import { CreatorResponseDto } from '@/app/common/general-types/creators.dto';
import { isPastCalendarDate, toScreenUntilInstant } from './sections/screening-window/screening-window.helpers';

type Screen = 'chooser' | 'ai' | 'wizard';

@Component({
  selector: 'app-screening-brief-select',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FlowChooserScreening, AiFirstScreening, WizardFirstScreening, ScreeningOverlay],
  templateUrl: './screening-brief-select.html',
  styleUrl: './screening-brief-select.scss',
})
export class ScreeningBriefSelect implements OnDestroy {

  /** Services */
  private router = inject(Router);
  private readonly location = inject(Location);
  public readonly createScreeningService = inject(CreateScreeningService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly firebaseService = inject(FirebaseService);
  private readonly recruitGroupService = inject(RecruitGroupService);
  private readonly socialScreeningService = inject(SocialScreeningService);
  private readonly alertService = inject(AlertService);
  private readonly briefService = inject(BriefService);
  /** Services - END */

  readonly screen = signal<Screen>('chooser');

  readonly submitStages = SUBMIT_STAGES;

  /** `idle` hides the overlay; `sending` spins; `done` shows the submitted state. */
  readonly submitStage = signal<'idle' | 'sending' | 'done'>('idle');
  readonly submittedBriefId = signal<string | undefined>(undefined);

  readonly briefName = computed(
    () => this.createScreeningService.basicInfoModel().briefTitle.trim() || 'Your screening brief',
  );

  readonly overlayResult = computed<'running' | 'pass'>(
    () => (this.submitStage() === 'done' ? 'pass' : 'running'),
  );

  /** The closing line of the submitted overlay, which depends on how creators were sourced. */
  readonly creatorLine = computed<string>(() => {
    if (this.createScreeningService.creatorSourceMethod() === 'later') {
      return 'You can add creators any time from Manage Brief.';
    }
    const queued = this.queuedCreatorCount();
    if (!queued) {
      return 'No creators added yet — add them from Manage Brief.';
    }
    return `${queued} ${queued === 1 ? 'creator is' : 'creators are'} queued for screening.`;
  });

  readonly submittedSub = computed(() => `is live. ${this.creatorLine()}`);

  private readonly subscriptionList = new Subscription();

  private queuedCreatorCount(): number {
    const manual = this.createScreeningService.manualCreators().length;
    if (manual) {
      return manual;
    }
    const list = this.createScreeningService.selectedList();
    if (list) {
      return list.numberOfCreators ?? list.creators?.length ?? 0;
    }
    return this.socialScreeningService.selectedCreators().length;
  }

  chooseFlow(flow: ScreeningFlow): void {
    this.screen.set(flow);
  }

  switchFlow(): void {
    this.screen.set('chooser');
  }

  /** Clears the AI draft so the user can start the AI-first flow again from scratch. */
  startOver(): void {
    const keepCreators = this.createScreeningService.usePreselectedCreators();
    this.createScreeningService.reset();
    this.createScreeningService.usePreselectedCreators.set(keepCreators);
  }

  submit(): void {

    console.log('Screening Submit')
    console.log('Screening Submit')
    console.log('Screening Submit')

    this.submitStage.set('sending');

    this.subscriptionList.add(
      this.resolveRecruitGroupId().pipe(
        switchMap((recruitGroupId) => {
          const newScreeningBrief: CreateSocialScreeningBriefDto = {
            briefTitle: this.createScreeningService.basicInfoModel().briefTitle,
            businessAccountId: this.currentUserService.focussedBrand.id,
            briefUrl: this.createScreeningService.requirementsDocumentUrl(),
            contentRequirements: this.createScreeningService.contentRequirementsHtml(),
            contentDonts: this.createScreeningService.contentDontsHtml(),
            briefText: this.createScreeningService.briefTextHtml(),
            productId: this.createScreeningService.selectedProduct()?.id,
            screenUntil: this.getScreeningUnit(),
            recruitGroupId,
            csv_link: this.createScreeningService.uploadedCsvUrl() ?? undefined,
            restrictions: this.toRestrictionPayload(),
            reportType: this.createScreeningService.reportType(),
            reportPrompt:
              this.createScreeningService.reportType() === 'custom'
                ? this.createScreeningService.reportPrompt().trim() || undefined
                : undefined,
          };
          return this.socialScreeningService.createSocialScreeningBrief(newScreeningBrief);
        }),
      ).subscribe({
        next: (response: unknown) => {
          this.submittedBriefId.set(this.readId(response));
          this.clearPreselectedCreators();
          this.submitStage.set('done');
        },
        error: (error: unknown) => {
          console.error('Error creating screening brief: ', error);
          this.submitStage.set('idle');
          const message = error instanceof Error && error.message
            ? error.message
            : 'We couldn\'t create the screening list. Each creator needs a name and an Instagram or TikTok handle.';
          void this.alertService.alert({
            title: 'Couldn\'t launch brief',
            message,
            type: 'alert',
            icon: 'error',
            confirmText: 'OK',
            cancelText: 'Close',
          });
        },
      }),
    );
  }

  /**
   * Calendar day sent as `screenUntil`.
   * A selected past date is posted as that day. Anything else falls back to 30 days ago.
   */
  getScreeningUnit(): Date {
    const selected = this.createScreeningService.screenContentUntil();
    const source = selected && isPastCalendarDate(selected)
      ? selected
      : new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    return toScreenUntilInstant(source);
  }

  exit(): void {
    this.location.back();
  }

  /** Submitted-overlay actions. */
  openManageBrief(): void {
    const briefId = this.submittedBriefId();
    this.submitStage.set('idle');
    if (briefId) {
      this.router.navigate(['/manage-brief-screen', briefId]);
    } else {
      this.location.back();
    }
  }

  goHome(): void {
    this.submitStage.set('idle');
    this.location.back();
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
    this.createScreeningService.reset();
    this.submitStage.set('idle');
    this.screen.set('chooser');
  }

  /** Strips the client-only row id before the restrictions go on the wire. */
  private toRestrictionPayload(): ScreeningRestrictionItem[] | undefined {
    const restrictions = this.createScreeningService.restrictions();
    if (!restrictions.length) {
      return undefined;
    }
    return restrictions.map(({ categoryId, categoryLabel, text, custom }) => ({
      categoryId,
      categoryLabel,
      text,
      custom,
    }));
  }

  /**
   * The recruit group this brief should use.
   *
   * Returns undefined when nothing was staged — the API then mints a brief-scoped group,
   * and the brief simply waits at Not_Ready until creators are added.
   */
  private resolveRecruitGroupId(): Observable<string | undefined> {
    const existingListId = this.createScreeningService.selectedList()?.id;
    if (existingListId) {
      return of(existingListId);
    }

    const manualCreators = this.createScreeningService.manualCreators();
    if (manualCreators.length) {
      return this.createListFrom(manualCreators, 'manual', 'Creators added by hand when this brief was created.');
    }

    if (!this.createScreeningService.usePreselectedCreators()) {
      return of(undefined);
    }

    const selectedCreators = this.socialScreeningService.selectedCreators();
    if (selectedCreators.length === 0) {
      return of(undefined);
    }

    const creators = selectedCreators
      .map((creator) => this.toListCreatorItem(creator))
      .filter((item) => this.isValidListCreatorItem(item));

    if (creators.length === 0) {
      return throwError(() => new Error(
        'Each selected creator needs an Instagram or TikTok handle before we can launch this screening brief.',
      ));
    }

    return this.createListFrom(
      creators,
      'select',
      'Creators selected for screening before this brief was created.',
    );
  }

  /**
   * Creates the recruit group a brief needs when creators were staged in this flow, then
   * adds those creators to it.
   */
  private createListFrom(
    creators: ScreeningListCreatorItem[],
    creatorSelectMethod: CreateSocialScreeningListDto['creatorSelectMethod'],
    listDescription: string,
  ): Observable<string | undefined> {
    const briefTitle = this.createScreeningService.basicInfoModel().briefTitle.trim();
    const creatorIds = creators
      .map((item) => item.blazeFirebaseUid ?? item.creatorId)
      .filter((id): id is string => !!id);
    // Hand-entered rows have no creator id; the API matches them by handle, else creates
    // a creator, so they must go up as rows rather than being dropped here.
    const handleRows = creators
      .filter((item) => !(item.blazeFirebaseUid ?? item.creatorId))
      .map(({ name, instagramHandle, tiktokHandle }) => ({ name, instagramHandle, tiktokHandle }));

    return this.recruitGroupService.createRecruitGroup({
      groupName: briefTitle ? `${briefTitle} creators` : 'Selected creators',
      groupDescription: listDescription,
      businessAccountId: this.currentUserService.focussedBrand.id,
      createdBy: this.firebaseService.userId ?? '',
      isGlobal: false,
      // A group backing a screening brief always needs a Noodle list.
      syncWithNoodle: true,
    }).pipe(
      switchMap((group) => {
        const groupId = this.readId(group);
        if (!groupId || (creatorIds.length === 0 && handleRows.length === 0)) {
          return of(groupId);
        }
        return this.recruitGroupService
          .addCreatorsToGroup(groupId, creatorIds, handleRows)
          .pipe(
            map((result) => {
              if (result.added.length + result.alreadyPresent.length === 0) {
                throw new Error(this.noCreatorsAddedMessage(result.failedCreators));
              }
              return groupId;
            }),
          );
      }),
    );
  }

  /** Why a staged list ended up empty, so the brief isn't launched with nobody to screen. */
  private noCreatorsAddedMessage(failed: { name: string; message: string }[] | undefined): string {
    const first = failed?.[0];
    return first
      ? `We couldn't add ${first.name || 'your creators'} to this brief: ${first.message}.`
      : 'None of these creators could be added to this brief. Each needs an Instagram or TikTok handle.';
  }

  private toListCreatorItem(creator: CreatorResponseDto): ScreeningListCreatorItem {
    const instagramHandle = this.socialScreeningService.instagramHandle(creator);
    const tiktokHandle = this.socialScreeningService.tiktokHandle(creator);
    const uid = creator.blazeFirebaseUid?.trim() || undefined;
    return {
      creatorId: uid,
      blazeFirebaseUid: uid,
      name: this.listCreatorName(creator, instagramHandle, tiktokHandle),
      instagramHandle,
      tiktokHandle,
    };
  }

  private isValidListCreatorItem(item: ScreeningListCreatorItem): boolean {
    return item.name.trim().length > 0 && (!!item.instagramHandle || !!item.tiktokHandle);
  }

  private listCreatorName(
    creator: CreatorResponseDto,
    instagramHandle: string | undefined,
    tiktokHandle: string | undefined,
  ): string {
    const firstName = this.usableName(creator.firstName);
    const lastName = this.usableName(creator.lastName);
    if (firstName && lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    if (firstName) {
      return firstName;
    }
    if (lastName) {
      return lastName;
    }
    if (instagramHandle) {
      return `@${instagramHandle}`;
    }
    if (tiktokHandle) {
      return `@${tiktokHandle}`;
    }
    return '';
  }

  private usableName(value: string | undefined): string {
    const trimmed = value?.trim() ?? '';
    if (!trimmed || /^[.\-_]+$/.test(trimmed)) {
      return '';
    }
    return trimmed;
  }

  private readId(response: unknown): string | undefined {
    if (typeof response !== 'object' || response === null || !('id' in response)) {
      return undefined;
    }
    const id = (response as { id: unknown }).id;
    return typeof id === 'string' ? id : undefined;
  }

  private clearPreselectedCreators(): void {
    this.socialScreeningService.selectedCreators.set([]);
    this.briefService.creatorsToScreen.set([]);
    this.createScreeningService.usePreselectedCreators.set(false);
  }

}
